import React from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * VSM Blueprint Industrial Icon Library — V35.0 Cyber-Industrial
 * Suporte a gradiente linear (#00E5FF → #00FF85) e sketch mode (traço fragmentado).
 */

export type VsmBlueprintIconProps = {
  width?: number;
  height?: number;
  color?: string;
  /** Aplica gradiente linear ciano→verde nos traçados */
  useGradient?: boolean;
  /** Modo sketch: traços fragmentados com cantos arredondados */
  sketchMode?: boolean;
  /** Cor de preenchimento (default: igual ao stroke) — usado em estilo técnico para tintar interior */
  fillColor?: string;
  /** Multiplicador da opacidade de fill (default 1); use >1 para tintar com cor escolhida */
  fillOpacityBoost?: number;
};

// Gradient constant ID — safe por ser scoped dentro de cada <svg>
const GRAD_ID = 'vsm-grad';

// Typography token: single source of truth for all in-icon text
const FONT_STACK = "'JetBrains Mono', 'IBM Plex Mono', 'Roboto Mono', ui-monospace, monospace";

// Stroke tokens (CAD line weight system)
const SW = { divider: 1, regular: 1.8 } as const;

const cad = (color: string, strokeWidth: number = SW.regular, sketch = false) => ({
  fill: 'none',
  stroke: color,
  strokeWidth,
  strokeLinecap: sketch ? 'round' as const : 'square' as const,
  strokeLinejoin: sketch ? 'round' as const : 'miter' as const,
  strokeDasharray: sketch ? '5 3' : undefined,
  vectorEffect: 'non-scaling-stroke' as const,
});

const thin = (color: string, strokeWidth: number = SW.divider, sketch = false) => ({
  fill: 'none',
  stroke: color,
  strokeWidth,
  strokeLinecap: sketch ? 'round' as const : 'square' as const,
  strokeLinejoin: sketch ? 'round' as const : 'miter' as const,
  strokeDasharray: sketch ? '3 2' : undefined,
  vectorEffect: 'non-scaling-stroke' as const,
});

const softFill = (color: string, opacity = 0.12) => ({
  fill: color,
  fillOpacity: opacity,
});

const useBlueprintCanvasBg = () => {
  const { isDark, theme } = useTheme();
  return theme === 'black' ? '#131314' : isDark ? '#223244' : 'white';
};

// Defs com gradiente linear 45° — injetado em todo Svg
const GradDefs: React.FC = () => (
  <defs>
    <linearGradient id={GRAD_ID} x1="0%" y1="100%" x2="100%" y2="0%" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.92" />
      <stop offset="52%" stopColor="#68F0FF" stopOpacity="0.96" />
      <stop offset="100%" stopColor="#00FF85" stopOpacity="0.9" />
    </linearGradient>
  </defs>
);

const Svg: React.FC<React.PropsWithChildren<VsmBlueprintIconProps & { viewBox: string; preserveAspectRatio?: string }>> = ({
  width,
  height,
  viewBox,
  preserveAspectRatio,
  children,
}) => (
  <svg viewBox={viewBox} width={width} height={height} preserveAspectRatio={preserveAspectRatio} fill="none" aria-hidden="true">
    <GradDefs />
    {children}
  </svg>
);

export const BpProcesso: React.FC<VsmBlueprintIconProps> = ({ width = 112, height = 90, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 148 112" width={width} height={height}>
      <rect x="2.9" y="2.9" width="142.2" height="106.2" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
      <line x1="2.9" y1="40" x2="145.1" y2="40" {...thin(c, SW.divider, s)} />
    </Svg>
  );
};

export const BpEstoque: React.FC<VsmBlueprintIconProps> = ({ width = 100, height = 76, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 84 72" width={width} height={height}>
      <path d="M42 4L79 68H5Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
    </Svg>
  );
};

export const BpExterno: React.FC<VsmBlueprintIconProps> = ({ width = 108, height = 86, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="5 17 98 62" width={width} height={height} preserveAspectRatio="none">
      <path d="M6 78V18L24 36V18L42 36V18L60 36V18L78 36V18L102 18V78Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
    </Svg>
  );
};

export const BpEntrega: React.FC<VsmBlueprintIconProps> = ({ width = 102, height = 54, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 112 70" width={width} height={height}>
      <rect x="9" y="18" width="58" height="32" {...cad(c, SW.regular, s)} {...softFill(fc, 0.1*fb)} />
      <path d="M67 30H88L103 44V50H67Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.1*fb)} />
      <path d="M73 35H87L94 43H73Z" {...thin(c, SW.divider, s)} {...softFill(fc, 0.08*fb)} />
      <path d="M9 50H103" {...thin(c, SW.divider, s)} />
      <circle cx="27" cy="56" r="9" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
      <circle cx="27" cy="56" r="3" {...thin(c, SW.divider, s)} />
      <circle cx="55" cy="56" r="9" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
      <circle cx="55" cy="56" r="3" {...thin(c, SW.divider, s)} />
      <circle cx="90" cy="56" r="9" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
      <circle cx="90" cy="56" r="3" {...thin(c, SW.divider, s)} />
    </Svg>
  );
};

export const BpCaminhao: React.FC<VsmBlueprintIconProps> = ({ width = 110, height = 56, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 124 68" width={width} height={height}>
      <path d="M8 18H74V48H8Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.09*fb)} />
      <path d="M74 26H96L114 38V48H74Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.09*fb)} />
      <path d="M80 30H94L102 38H80Z" {...thin(c, SW.divider, s)} {...softFill(fc, 0.06*fb)} />
      <path d="M8 48H114" {...thin(c, SW.divider, s)} />
      <circle cx="28" cy="54" r="8" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
      <circle cx="28" cy="54" r="3" {...thin(c, SW.divider, s)} />
      <circle cx="58" cy="54" r="8" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
      <circle cx="58" cy="54" r="3" {...thin(c, SW.divider, s)} />
      <circle cx="94" cy="54" r="8" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
      <circle cx="94" cy="54" r="3" {...thin(c, SW.divider, s)} />
    </Svg>
  );
};

export const BpOperador: React.FC<VsmBlueprintIconProps> = ({ width = 56, height = 64, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    // Cabeça (círculo) + arco de ombros abaixo — forma VSM simplificada
    <Svg viewBox="0 0 56 64" width={width} height={height}>
      <circle cx="28" cy="16" r="14" {...cad(c, SW.regular, s)} />
      <path d="M4 60 A28 28 0 0 1 52 60" {...cad(c, SW.regular, s)} />
    </Svg>
  );
};

export const BpSupermercado: React.FC<VsmBlueprintIconProps> = ({ width = 54, height = 100, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 71.469039 131.59151" width={width} height={height}>
      <path d="m 0.5867126,131.0646 -0.56098,-1.091 0.55458,-1.0787 0.55457,-1.0786 H 68.369843 l -0.0684,-39.415474 -67.8755504,0.13746 -0.24318,-3.32739 68.1456704,-0.5653 0.0703,-39.24598 -68.0753904,0.4187 -0.3191,-3.186816 68.2269804,-0.5654 0.13863,-38.2909003 H 1.2602083e-5 L 0.3024226,1.8875997 0.6048426,-2.7122336e-7 H 71.469043 V 131.5915 L 1.1476526,131.36051 Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.1*fb)} />
    </Svg>
  );
};

export const BpPulmao: React.FC<VsmBlueprintIconProps> = ({ width = 54, height = 100, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 71.469039 131.59151" width={width} height={height}>
      <g transform="translate(-508.92421,493.27913)">
        <path d="m 509.51095,-362.21453 -0.56098,-1.091 0.55458,-1.0787 0.55457,-1.0786 h 67.23496 l -0.0684,-39.41547 -67.87555,0.13746 -0.24318,-3.32739 68.14567,-0.5653 0.0703,-39.24598 -68.07539,0.4187 -0.3191,-3.18682 68.22698,-0.5654 0.13863,-38.2909 h -68.36979 l 0.30241,-1.8876 0.30242,-1.8876 h 70.8642 v 131.5915 l -70.32139,-0.23099 z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.1*fb)} />
        <path d="m 579.80658,-362.21453 0.56098,-1.091 -0.55458,-1.0787 -0.55457,-1.0786 h -67.23496 l 0.0684,-39.41547 67.87555,0.13746 0.24318,-3.32739 -68.14567,-0.5653 -0.0703,-39.24598 68.07539,0.4187 0.3191,-3.18682 -68.22698,-0.5654 -0.13863,-38.2909 h 68.36979 l -0.30241,-1.8876 -0.30242,-1.8876 h -70.8642 v 131.5915 l 70.32139,-0.23099 z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.1*fb)} />
      </g>
    </Svg>
  );
};

export const BpEmpurrado: React.FC<VsmBlueprintIconProps> = ({ width = 128, height = 46, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 179.28923 71.456154" width={width} height={height}>
      <path
        d="m 41.899281,23.473499 h 10.1747 v 25.4095 h -10.1747 z m 74.763199,0.0155 h 10.1746 v 25.4095 h -10.1746 z m -25.189899,-0.0155 h 10.174599 v 25.4095 H 91.472581 Z m -25.0691,0 h 10.1745 v 25.4095 h -10.1745 z m -49.0082,0 h 10.1746 v 25.4095 h -10.1746 z M 0.94648054,20.127869 v 31.95087 l 149.19349946,-0.008 -0.079,16.786 27.9913,-33.09955 -27.7985,-33.2050497 -0.3079,17.7089397 z"
        fill={c}
        stroke={c}
        strokeWidth={SW.regular}
        strokeDasharray={s ? '5 3' : undefined}
        strokeLinecap={s ? 'round' : 'square'}
        strokeLinejoin={s ? 'round' : 'miter'}
        vectorEffect="non-scaling-stroke"
      />
    </Svg>
  );
};

export const BpFifo: React.FC<VsmBlueprintIconProps> = ({ width = 112, height = 46, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 112 46" width={width} height={height}>
      <path d="M5 12H77V4L107 23L77 42V34H5Z" {...cad(c, SW.regular, s)} />
      <text x="43" y="29" textAnchor="middle" fontSize="16" fontFamily={FONT_STACK} letterSpacing="2.6" fontWeight="400" fill={color} stroke="none">FIFO</text>
    </Svg>
  );
};

export const BpPuxado: React.FC<VsmBlueprintIconProps> = ({ width = 64, height = 64, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 66.792525 60.173996" width={width} height={height}>
      {/* Ponta de seta (filled) */}
      <path d="m 58.762221,29.765009 6.8408,-11.53772 -15.4763,1.23837 z" fill={color} stroke="none" />
      {/* Arco circular */}
      <path d="m 60.007221,28.600079 c 0,-16.25028 -10.1936,-27.95532997 -27.68,-27.95532997 -17.48646,0 -31.68244953,13.19243997 -31.68244953,29.44270997 0,16.25027 14.19598953,29.44174 31.68244953,29.44174 7.8518,0 15.2649,-2.48543 20.2845,-7.61107 0.1183,-0.94433 -0.4478,-1.37727 -1.2744,-0.85198 -2.1778,1.38395 -7.7233,6.72777 -19.0101,6.72777 -16.47731,0 -29.8151595,-12.394 -29.8151595,-27.70646 0,-15.31246 13.3378495,-27.70742 29.8151595,-27.70742 16.4773,0 25.8127,10.90758 25.8127,26.22004 z" {...cad(c, SW.regular, s)} />
    </Svg>
  );
};

export const BpInfoEletro: React.FC<VsmBlueprintIconProps> = ({ width = 126, height = 40, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 220 60" width={width} height={height}>
      <path d="M8 30H52L76 14L100 46L124 14L148 46L172 30H198" {...cad(c, SW.regular, s)} />
      <path d="M198 18L212 30L198 42" {...cad(c, SW.regular, s)} />
    </Svg>
  );
};

export const BpInfoManual: React.FC<VsmBlueprintIconProps> = ({ width = 132, height = 34, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 173.62337 60.228653" width={width} height={height}>
      <path
        d="M 171.1485,30.158308 145.30022,4.2276476 145.21782,56.006588 Z M -1.4014943e-5,29.815248 H 146.19356"
        fill={c}
        stroke={c}
        strokeWidth={SW.regular}
        strokeDasharray={s ? '5 3' : undefined}
        strokeLinecap={s ? 'round' : 'square'}
        strokeLinejoin={s ? 'round' : 'miter'}
        vectorEffect="non-scaling-stroke"
      />
    </Svg>
  );
};

export const BpKaizen: React.FC<VsmBlueprintIconProps> = ({ width = 64, height = 64, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 64 64" width={width} height={height}>
      <path d="M32 4L38 22L55 13L49 31L61 42L43 45L39 61L28 49L10 56L17 39L3 28L22 24Z" {...cad(c, SW.regular, s)} />
      <path d="M23 32H41M32 23V41" {...cad(c, SW.regular, s)} />
    </Svg>
  );
};

// Kanban de Retirada — carta com canto dobrado e hachura diagonal /
// Geometria fiel ao SVG canônico icones-vsm-blueprint/kanban-retirada.svg
export const BpKanbanRet: React.FC<VsmBlueprintIconProps> = ({ width = 52, height = 64, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  const patternId = `bp-kanban-ret-hatch-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <Svg viewBox="0 0 88.310462 65.697579" width={width} height={height}>
      <defs>
        <pattern id={patternId} width="8" height="8" patternUnits="userSpaceOnUse">
          {/* Hachura única direção / (NE) — igual ao SVG canônico */}
          <line x1="0" y1="8" x2="8" y2="0" stroke={c} strokeWidth="0.9" />
        </pattern>
      </defs>
      {/* Fundo suave */}
      <path d="M 1.8284941,1.8285008 H 55.751594 l 30.73035,30.4404402 v 31.60012 H 1.8284941 Z" {...softFill(fc, 0.11*fb)} stroke="none" />
      {/* Hachura diagonal preenchendo o pentágono */}
      <path d="M 1.8284941,1.8285008 H 55.751594 l 30.73035,30.4404402 v 31.60012 H 1.8284941 Z" fill={`url(#${patternId})`} stroke="none" />
      {/* Contorno + linha do dobramento */}
      <path d="M 1.8284941,1.8285008 H 55.751594 l 30.73035,30.4404402 v 31.60012 H 1.8284941 Z m 53.9231,0.6522 c 0,28.4111 1e-5,29.4258 1e-5,29.4258 l 29.86063,-0.14496" {...cad(c, SW.regular, s)} />
    </Svg>
  );
};

// Kanban de Produção — carta pentágono com canto recortado (dog-ear)
export const BpKanbanProd: React.FC<VsmBlueprintIconProps> = ({ width = 52, height = 64, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 88.310393 65.697556" width={width} height={height}>
      <path d="M 1.8284383,1.8285097 H 55.751538 L 86.481888,32.26895 V 63.86907 H 1.8284383 Z" {...softFill(fc, 0.11*fb)} stroke="none" />
      <path d="M 1.8284383,1.8285097 H 55.751538 L 86.481888,32.26895 V 63.86907 H 1.8284383 Z m 53.923100,0.652210 c 0,28.4111003 1e-5,29.4257803 1e-5,29.4257803 l 29.86063,-0.14496" {...cad(c, SW.regular, s)} />
    </Svg>
  );
};

// Kanban chegando em Lotes — 3 cards empilhados + seta horizontal
export const BpKanbanLote: React.FC<VsmBlueprintIconProps> = ({ width = 96, height = 60, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 186.46316 81.203751" width={width} height={height}>
      <path d="m 183.13712,41.549645 -27.6589,-9.91879 -0.088,19.80606 z M -1.7435421e-5,41.418425 H 156.43412" {...cad(c, SW.regular, s)} />
      <path d="m 52.727463,1.8285251 h 53.923057 l 30.7304,30.4404399 v 31.60012 H 52.727463 Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.11*fb)} />
      <path d="m 37.221283,9.9792051 h 53.92314 L 121.87472,40.419645 v 31.60012 H 37.221283 Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.09*fb)} />
      <path d="m 22.112713,17.334695 h 53.92311 l 30.730297,30.44044 v 31.60012 H 22.112713 Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.07*fb)} />
    </Svg>
  );
};

// Posto Kanban — suporte em T com pino de cartolina
export const BpKanbanPosto: React.FC<VsmBlueprintIconProps> = ({ width = 44, height = 62, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  // ViewBox: 0 0 43.867217 61.091263
  // Caixa (box): x≈3–41, y≈25–58 → cartão kanban portrait centralizado dentro da caixa
  const kx0 = 10, ky0 = -8, kw = 24, kh = 28, kfold = 7;
  const kPath = `M ${kx0} ${ky0} L ${kx0 + kw - kfold} ${ky0} L ${kx0 + kw} ${ky0 + kfold} L ${kx0 + kw} ${ky0 + kh} L ${kx0} ${ky0 + kh} Z`;
  return (
    <Svg viewBox="0 0 43.867217 61.091263" width={width} height={height}>
      {/* Contorno do posto kanban */}
      <path
        d="m 4.1548582,58.391551 16.8330498,-0.38095 -0.064,-32.59343 L 3.1442582,24.877371 0.93924817,1.1259908 H 1.8176482 l 1.45941,0.0648 2.12621,22.1427702 16.4847198,0.19436 h 16.66217 l 2.31607,-22.67182022 h 1.23175 0.82824 l -2.30061,24.29120022 -17.70148,0.27839 0.39946,32.65022 16.69637,0.26997 0.30447,1.88928 H 22.093958 3.8634882 Z"
        fill={c}
        stroke={c}
        strokeWidth={SW.regular}
        strokeDasharray={s ? '5 3' : undefined}
        strokeLinecap={s ? 'round' : 'square'}
        strokeLinejoin={s ? 'round' : 'miter'}
        vectorEffect="non-scaling-stroke"
      />
      {/* Cartão kanban produção portrait (girado 90°), dobra no canto superior direito */}
      <path d={kPath} fill="none" stroke={c} strokeWidth={SW.thin} vectorEffect="non-scaling-stroke" />
      <polyline points={`${kx0 + kw - kfold},${ky0} ${kx0 + kw - kfold},${ky0 + kfold} ${kx0 + kw},${ky0 + kfold}`} fill="none" stroke={c} strokeWidth={SW.thin} vectorEffect="non-scaling-stroke" />
    </Svg>
  );
};

export const BpKanbanSinal: React.FC<VsmBlueprintIconProps> = ({ width = 70, height = 60, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 81.109954 67.232071" width={width} height={height}>
      <g transform="matrix(-0.57365286,-0.82121723,0.56242133,-0.82477359,866.55615,-169.59123)">
        <path d="m 587.93112,-868.48588 70.17449,4.92927 -65.68768,65.33683 z" {...cad(c, SW.regular, s)} />
      </g>
    </Svg>
  );
};

export const BpNivelamento: React.FC<VsmBlueprintIconProps> = ({ width = 112, height = 62, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 122.86093 51.169922" width={width} height={height}>
      <rect x="35.5" y="11.5" width="51" height="28" {...cad(c, SW.regular, s)} {...softFill(fc, 0.1*fb)} />
      <text x="61.430465" y="30.6" textAnchor="middle" fontSize="12" fontFamily="Arial, sans-serif" fill={c} stroke="none">OXOX</text>
    </Svg>
  );
};

export const BpVaver: React.FC<VsmBlueprintIconProps> = ({ width = 92, height = 52, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 112 60" width={width} height={height}>
      <circle cx="30" cy="30" r="18" {...cad(c, SW.regular, s)} {...softFill(fc, 0.05*fb)} />
      <circle cx="82" cy="30" r="18" {...cad(c, SW.regular, s)} {...softFill(fc, 0.05*fb)} />
      <path d="M48 22H64L72 30L64 38H48L40 30Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.08*fb)} />
    </Svg>
  );
};

export const BpComunicacao: React.FC<VsmBlueprintIconProps> = ({ width = 76, height = 64, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  return (
    <Svg viewBox="0 0 76 64" width={width} height={height}>
      <path d="M12 10H56L66 20V56H12Z" {...cad(c, SW.regular, s)} {...softFill(fc, 0.11*fb)} />
      <path d="M56 10V20H66" {...thin(c, SW.divider, s)} />
      <path d="M22 25H52M22 35H45M22 45H38" {...thin(c, SW.divider, s)} />
    </Svg>
  );
};

export const BpDatabox: React.FC<VsmBlueprintIconProps> = ({ width = 92, height = 140, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  const processDividerY = 212 * (104 / 207);
  return (
    <Svg viewBox="0 0 148 212" width={width} height={height} preserveAspectRatio="none">
      <rect x="2.9" y="2.9" width="142.2" height="206.2" {...cad(c, SW.regular, s)} />
      <rect x="2.9" y="2.9" width="142.2" height={processDividerY - 2.9} {...softFill(fc, 0.08*fb)} stroke="none" />
      <line x1="2.9" y1="40" x2="145.1" y2="40" {...thin(c, SW.divider, s)} />
      <line x1="2.9" y1={processDividerY} x2="145.1" y2={processDividerY} {...thin(c, SW.divider, s)} />
      <line x1="2.9" y1="137" x2="145.1" y2="137" {...thin(c, SW.divider, s)} />
      <line x1="2.9" y1="162" x2="145.1" y2="162" {...thin(c, SW.divider, s)} />
      <line x1="2.9" y1="187" x2="145.1" y2="187" {...thin(c, SW.divider, s)} />
    </Svg>
  );
};

export const BpCelula: React.FC<VsmBlueprintIconProps> = ({ width = 230, height = 179, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const strokeColor = useGradient ? `url(#${GRAD_ID})` : color;
  const sketch = sketchMode ?? false;
  const fc = fillColor ?? strokeColor;
  const fb = fillOpacityBoost ?? 1;
  const fillOpacity = Math.min(1, 0.08 * fb);
  return (
    <Svg viewBox="0 0 299.44125 232.99123" width={width} height={height} preserveAspectRatio="none">
      {/* Solid fill layer (no stroke) — title bar */}
      <rect x="1.4223887" y="1.4210055" width="296.59787" height="40.54726" fill={fc} fillOpacity={fillOpacity} stroke="none" />
      {/* Solid fill layer (no stroke) — U-body around databox cutout */}
      <path d="M 1.4173572,41.723192 V 149.07426 H 63.658557 V 107.29399 H 236.61266 v 41.78027 h 61.4107 V 41.723192 Z" fill={fc} fillOpacity={fillOpacity} stroke="none" />
      {/* Stroke layer — title bar outline */}
      <rect x="1.4223887" y="1.4210055" width="296.59787" height="40.54726" {...cad(strokeColor, SW.regular, sketch)} />
      {/* Stroke layer — U-body outline */}
      <path d="M 1.4173572,41.723192 V 149.07426 H 63.658557 V 107.29399 H 236.61266 v 41.78027 h 61.4107 V 41.723192 Z" {...cad(strokeColor, SW.regular, sketch)} />
      <path d="M 79.104457,203.91333 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.42741 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.07573 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.25167 H 222.56616 v 27.46934 H 79.104457 Z" {...thin(strokeColor, SW.divider, sketch)} fill="none" />
      <path d="M 77.564657,121.99611 H 222.70616 v 109.2251 H 77.564657 Z" {...cad(strokeColor, SW.regular, sketch)} fill="none" />
    </Svg>
  );
};

export const BpProduto: React.FC<VsmBlueprintIconProps> = ({ width = 100, height = 46, color = 'currentColor', useGradient, sketchMode, fillColor, fillOpacityBoost }) => {
  const c = useGradient ? `url(#${GRAD_ID})` : color;
  const s = sketchMode ?? false;
  const fc = fillColor ?? c;
  const fb = fillOpacityBoost ?? 1;
  const isVertical = height > width;
  const path = 'm 0.97697686,20.36541 v 33.72815 l 128.50690314,-0.008 -0.068,17.71972 52.5348,-34.94072 -52.3686,-35.0521004 -0.2652,18.6940104 z';
  if (isVertical) {
    return (
      <Svg viewBox="0 0 73.633118 183.711" width={width} height={height} preserveAspectRatio="none">
        <g transform="translate(73.633118 0) rotate(90)">
          <path d={path} {...cad(c, SW.regular, s)} />
        </g>
      </Svg>
    );
  }
  return (
    <Svg viewBox="0 0 183.711 73.633118" width={width} height={height}>
      <path d={path} {...cad(c, SW.regular, s)} />
    </Svg>
  );
};

export const VSM_BLUEPRINT_MAP: Record<string, React.FC<VsmBlueprintIconProps>> = {
  processo: BpProcesso,
  estoque: BpEstoque,
  externo: BpExterno,
  fornecedor: BpExterno,
  cliente: BpExterno,
  entrega: BpEntrega,
  caminhao: BpCaminhao,
  transporte: BpCaminhao,
  operador: BpOperador,
  supermercado: BpSupermercado,
  pulmao: BpPulmao,
  empurrado: BpEmpurrado,
  puxado: BpPuxado,
  fifo: BpFifo,
  info_eletro: BpInfoEletro,
  info_manual: BpInfoManual,
  kaizen: BpKaizen,
  kanban_prod: BpKanbanProd,
  kanban_ret: BpKanbanRet,
  kanban_lote: BpKanbanLote,
  kanban_posto: BpKanbanPosto,
  kanban_sinal: BpKanbanSinal,
  nivelamento: BpNivelamento,
  vaver: BpVaver,
  comunicacao: BpComunicacao,
  databox: BpDatabox,
  celula: BpCelula,
  produto: BpProduto,
};
